/**
 * @suite/settings — the shared settings row primitives.
 *
 * The Kor panel's SettingItem / SettingToggle vocabulary, centralized: every
 * application's settings sections compose these instead of hand-rolling
 * rows and switches. They are thin React contracts over the served
 * `suite-ui.css` classes (`suite-ui-group`, `suite-ui-row`, `suite-ui-switch`)
 * plus the shell controls from `suite-settings.css` — all styling lives in
 * those sheets, none in the components.
 */

import { type ChangeEvent, type ReactNode } from "react";

/** An inset grouped card — one settings group with an optional title. */
export function SettingGroup({
    title,
    children,
}: {
    title?: string;
    children: ReactNode;
}) {
    return (
        <section className="suite-ui-group">
            {title ? <h3 className="suite-ui-group-title">{title}</h3> : null}
            {children}
        </section>
    );
}

/** One labeled row: label + optional hint on the left, control on the right. */
export function SettingRow({
    label,
    hint,
    control,
}: {
    label: ReactNode;
    hint?: ReactNode;
    control?: ReactNode;
}) {
    return (
        <div className="suite-ui-row">
            <div className="suite-ui-row-label">
                <span>{label}</span>
                {hint ? <span className="suite-ui-row-hint">{hint}</span> : null}
            </div>
            {control ? <div className="suite-ui-row-control">{control}</div> : null}
        </div>
    );
}

/** The CSS-only switch from `suite-ui.css`; behavior (aria-checked, click) here. */
export function SettingToggle({
    checked,
    onChange,
    label,
    disabled = false,
}: {
    checked: boolean;
    onChange: (next: boolean) => void;
    /** Accessible name when the row itself does not label the control */
    label?: string;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked ? "true" : "false"}
            aria-label={label}
            disabled={disabled}
            className="suite-ui-switch"
            onClick={() => onChange(!checked)}
        />
    );
}

export interface SettingSelectOption {
    value: string;
    label: string;
}

export function SettingSelect({
    value,
    options,
    onChange,
    label,
    disabled = false,
}: {
    value: string;
    options: readonly SettingSelectOption[];
    onChange: (next: string) => void;
    /** Accessible name when the row itself does not label the control */
    label?: string;
    disabled?: boolean;
}) {
    const handle = (event: ChangeEvent<HTMLSelectElement>) =>
        onChange(event.target.value);
    return (
        <select
            className="suite-settings-select"
            value={value}
            onChange={handle}
            aria-label={label}
            disabled={disabled}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

export function SettingTextField({
    value,
    onChange,
    placeholder,
    label,
    type = "text",
    disabled = false,
}: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    /** Accessible name when the row itself does not label the control */
    label?: string;
    type?: "text" | "password" | "url";
    disabled?: boolean;
}) {
    const handle = (event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.target.value);
    return (
        <input
            className="suite-settings-input"
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={handle}
            aria-label={label}
            disabled={disabled}
        />
    );
}

/** A number row control (spinner input). */
export function SettingNumberField({
    value,
    onChange,
    min,
    max,
    step = 1,
    label,
    disabled = false,
}: {
    value: number;
    onChange: (next: number) => void;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    disabled?: boolean;
}) {
    const handle = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        // An empty box means "still typing", not a request to commit 0; typed
        // values respect the same bounds as the spinner arrows.
        if (raw.trim() === "") return;
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) return;
        onChange(Math.min(Math.max(parsed, min ?? -Infinity), max ?? Infinity));
    };
    return (
        <input
            className="suite-settings-input"
            type="number"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={handle}
            aria-label={label}
            disabled={disabled}
        />
    );
}
